import React, { useEffect, useState } from 'react';
import { DataGridPro } from '@mui/x-data-grid-pro';

import { usePutAcquisitionsMutation } from '../../../store/slices/apiSlice/acquisitions-api-slice.js';
import { datagridsx } from '../../../constants/datagridsx.js';
import { colors } from '../../../constants/index.js';
import NumberDaysEdit from '../components/numberDaysEdit.js';
import TableButton from '../components/acquisitionsTableButton.js';
import AddAssumptionButton from '../components/addAssumptionButton.js';
import { handleBackendError } from '../../../utilities/errorHandling.js';
import { toastSuccess } from '../../../utilities/toast.js';

const LabelColumnWidth = 180;
const MilestoneColumnWidth = 140;

const columnDefinitions = [
    {
        field: 'label',
        headerName: '',
        type: 'text',
        width: LabelColumnWidth,
        renderCell: (params) => (
            <span style={{ color: colors.skyfallDark, fontWeight: 'bold' }}>
                {params.value}
            </span>
        ),
    },
    {
        field: 'offerToPermit',
        headerName: 'Offer to Permit',
        type: 'number',
        width: MilestoneColumnWidth,
        headerAlign: 'center',
        align: 'center',
        editable: true,
        renderEditCell: NumberDaysEdit,
    },
    {
        field: 'horizontalConstruction',
        headerName: 'Horizontal Construction',
        type: 'number',
        width: MilestoneColumnWidth,
        headerAlign: 'center',
        align: 'center',
        editable: true,
        renderEditCell: NumberDaysEdit,
    },
    {
        field: 'verticalConstruction',
        headerName: 'Vertical Construction',
        type: 'number',
        width: MilestoneColumnWidth,
        headerAlign: 'center',
        align: 'center',
        editable: true,
        renderEditCell: NumberDaysEdit,
    },
    {
        field: 'listToCoe',
        headerName: 'List To COE',
        type: 'number',
        width: MilestoneColumnWidth,
        headerAlign: 'center',
        align: 'center',
        editable: true,
        renderEditCell: NumberDaysEdit,
    },
    {
        field: 'total',
        headerName: 'Total',
        type: 'number',
        width: MilestoneColumnWidth,
        headerAlign: 'center',
        align: 'center',
    },
];

const determineAssumptionWithTotal = (assumption) => {
    const {
        offerToPermit,
        horizontalConstruction,
        verticalConstruction,
        listToCoe,
    } = assumption;
    const total =
    offerToPermit + horizontalConstruction + verticalConstruction + listToCoe;
    return { ...assumption, total };
};

const AcquisitionsAssumptions = ({ assumptions }) => {
    const [updateAcquisitions, updateAcquisitionsResponse] =
    usePutAcquisitionsMutation();

    const [dirty, setDirty] = useState(false);

    const [tableRows, setTableRows] = useState(() => {
        return assumptions.map((assumption, index) => {
            return determineAssumptionWithTotal({ ...assumption, id: index });
        });
    });

    useEffect(() => {
        if (updateAcquisitionsResponse) {
            const { isError, status, isSuccess } = updateAcquisitionsResponse;
            if (isError) {
                handleBackendError(
                    'Update Acquisitions Error',
                    updateAcquisitionsResponse
                );
            } else if (status === 'fulfilled' && isSuccess) {
                toastSuccess('Acquisitions Updated');
            }
        }
    }, [updateAcquisitionsResponse]);

    const handleSave = () => {
    // eslint-disable-next-line no-unused-vars
        const updates = tableRows.map(({ id, total, ...rest }) => rest);
        updateAcquisitions({ assumptions: [...updates] });
        setDirty(false);
    };

    const handleReset = () => {
        const defaultAssumptions = assumptions.map((assumption, index) => {
            return determineAssumptionWithTotal({ ...assumption, id: index });
        });
        setTableRows(defaultAssumptions);
        setDirty(false);
    };

    const handleAdd = (name) => {
        setTableRows((prev) => [
            ...prev,
            {
                label: name,
                id: prev.length,
                offerToPermit: '',
                horizontalConstruction: '',
                verticalConstruction: '',
                listToCoe: '',
                total: '',
            },
        ]);
        setDirty(true);
    };

    const containerWidth = `${
        5 * MilestoneColumnWidth + LabelColumnWidth + 20
    }px`;
    return (
        <div
            className="acquisitions-assumptions-container"
            style={{ '--assumptions-container-width': containerWidth }}
        >
            <div className="d-flex w-100 justify-content-end mb-2">
                <AddAssumptionButton onAdd={handleAdd} />
            </div>
            <DataGridPro
                rows={tableRows}
                columns={columnDefinitions}
                disableColumnSorting
                disableColumnMenu={true}
                editMode="cell"
                experimentalFeatures={{ newEditingApi: true }}
                processRowUpdate={(newRow, oldRow) => {
                    if (JSON.stringify(newRow) !== JSON.stringify(oldRow)) {
                        setDirty(true);
                        const updatedRow = determineAssumptionWithTotal({ ...newRow });
                        setTableRows((prev) =>
                            prev.map((r) => (r.id === updatedRow.id ? updatedRow : r))
                        );
                        return updatedRow;
                    }
                    return newRow;
                }}
                sx={{
                    ...datagridsx,
                    '& .MuiDataGrid-footerContainer': {
                        display: 'none',
                    },
                    '& .MuiDataGrid-columnHeader': {
                        justifyContent: 'center',
                        textAlign: 'center',
                    },
                }}
            />
            <div className="mt-4 w-100 d-flex justify-content-center align-items-center gap-4">
                <TableButton label="Save" disabled={!dirty} onClick={handleSave} />
                <TableButton
                    label="Reset"
                    disabled={!dirty}
                    onClick={handleReset}
                ></TableButton>
            </div>
        </div>
    );
};

export default AcquisitionsAssumptions;
